import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Webhook Subscriptions management controller.
//
// Backs both the index page (table + create/edit/delete/reenable
// modals) and the detail page (deliveries log, test-delivery button,
// delivery detail modal, retry actions).
//
// Mixpanel events fire both server-side (authoritative counts) and
// client-side (for funnel analysis). Server-side is the source of
// truth for CRUD; client-side covers the "interacted with UI" signal.

export default class extends Controller {
  static targets = [
    "rows", "row", "table", "emptyState",
    "createModal", "createForm", "createSubmit",
    "editModal", "editForm", "editSubmit", "editUrl", "editEventsContainer",
    "revealModal", "revealInput", "copyButton", "copyLabel",
    "deleteModal", "deleteUrl", "deleteConfirm",
    "reenableModal", "reenableUrl", "reenableConfirm",

    // Detail page only
    "testEventType", "testButton", "testButtonLabel", "testResult",
    "filterEventType", "filterStatus",
    "deliveriesEmpty", "deliveriesTableWrap", "deliveryRows",
    "deliveryModal", "deliveryModalMeta", "deliveryRequestBody",
    "deliveryResponseBody", "deliveryError", "deliveryErrorBody"
  ]

  static values = {
    createUrl: String,
    destroyUrlTemplate: String,
    updateUrlTemplate: String,
    reenableUrlTemplate: String,
    detailMode: Boolean,
    subscriptionId: String,
    deliverTestUrl: String,
    deliveriesUrl: String,
    deliveryRetryUrlTemplate: String,
    deliveryShowUrlTemplate: String
  }

  connect() {
    this._pendingDeleteId = null
    this._pendingReenableId = null
    this._pendingEditId = null
    this._openDeliveryId = null
    this._showPendingFlash()
  }

  // ── Modal plumbing ────────────────────────────────────────────

  stopPropagation(event) {
    event.stopPropagation()
  }

  openCreateModal() {
    this.createModalTarget.classList.remove("hidden")
    const urlInput = this.createFormTarget.querySelector("input[name='webhook_subscription[url]']")
    setTimeout(() => urlInput?.focus(), 50)
  }

  closeCreateModal() {
    this.createModalTarget.classList.add("hidden")
    this.createFormTarget.reset()
  }

  closeRevealModal() {
    this.revealModalTarget.classList.add("hidden")
    this.revealInputTarget.value = ""
  }

  openEditModal(event) {
    const { id, url, eventTypes } = event.params
    this._pendingEditId = id
    this.editUrlTarget.value = url
    const types = (eventTypes || "").split(",").filter(Boolean)
    this.editEventsContainerTarget
      .querySelectorAll('input[type="checkbox"][name="webhook_subscription[event_types][]"]')
      .forEach(cb => { cb.checked = types.includes(cb.value) })
    this._syncAllGroupCheckboxes(this.editEventsContainerTarget)
    this.editModalTarget.classList.remove("hidden")
    setTimeout(() => this.editUrlTarget.focus(), 50)
  }

  closeEditModal() {
    this.editModalTarget.classList.add("hidden")
    this._pendingEditId = null
  }

  openDeleteModal(event) {
    this._pendingDeleteId = event.params.id
    this.deleteUrlTarget.textContent = event.params.url
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDeleteModal() {
    this.deleteModalTarget.classList.add("hidden")
    this._pendingDeleteId = null
  }

  openReenableModal(event) {
    this._pendingReenableId = event.params.id
    this.reenableUrlTarget.textContent = event.params.url
    this.reenableModalTarget.classList.remove("hidden")
  }

  closeReenableModal() {
    this.reenableModalTarget.classList.add("hidden")
    this._pendingReenableId = null
  }

  // ── Create flow ───────────────────────────────────────────────

  async submitCreate(event) {
    event.preventDefault()
    const form = this.createFormTarget
    const urlField = form.querySelector("input[name='webhook_subscription[url]']")
    if (urlField && !urlField.value.startsWith("https://")) {
      this._showToast("Webhook URL must start with https://", "error")
      return
    }

    const data = new FormData(form)
    if (!data.getAll("webhook_subscription[event_types][]").length) {
      this._showToast("Select at least one event type.", "error")
      return
    }

    this.createSubmitTarget.disabled = true
    this.createSubmitTarget.textContent = "Creating…"

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        body: data
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error((json.errors && json.errors.join(", ")) || "Failed to create webhook")
      }

      this._insertOrReplaceRow(json.id, json.row_html)
      this.closeCreateModal()
      this.revealInputTarget.value = json.secret
      this.revealModalTarget.classList.remove("hidden")
      this._resetCopyButton()
      this._showToast("Webhook created")

      track("Webhook Subscription Created", {
        webhook_subscription_id: json.id,
        event_count: (json.event_types || []).length
      })
    } catch (e) {
      this._showToast(e.message || "Failed to create webhook", "error")
    } finally {
      this.createSubmitTarget.disabled = false
      this.createSubmitTarget.textContent = "Create webhook"
    }
  }

  // ── Edit flow ─────────────────────────────────────────────────

  async submitEdit(event) {
    event.preventDefault()
    if (!this._pendingEditId) return

    const urlValue = this.editUrlTarget.value
    if (!urlValue.startsWith("https://")) {
      this._showToast("Webhook URL must start with https://", "error")
      return
    }

    const checked = Array.from(
      this.editEventsContainerTarget.querySelectorAll(
        'input[type="checkbox"][name="webhook_subscription[event_types][]"]:checked'
      )
    ).map(cb => cb.value)

    if (!checked.length) {
      this._showToast("Select at least one event type.", "error")
      return
    }

    const data = new FormData()
    data.append("webhook_subscription[url]", urlValue)
    checked.forEach(v => data.append("webhook_subscription[event_types][]", v))

    this.editSubmitTarget.disabled = true
    this.editSubmitTarget.textContent = "Saving…"

    try {
      const url = this.updateUrlTemplateValue.replace("ID", this._pendingEditId)
      const response = await fetch(url, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        body: data
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error((json.errors && json.errors.join(", ")) || "Failed to update webhook")
      }

      if (this.hasRowsTarget && json.row_html) {
        this._insertOrReplaceRow(json.id, json.row_html)
      }

      track("Webhook Subscription Updated", {
        webhook_subscription_id: json.id,
        event_count: (json.event_types || []).length
      })

      this.closeEditModal()
      if (this.detailModeValue) {
        // On the detail page, URL + event list are rendered server-side
        // inside a non-target card; easiest refresh is a soft reload
        // (with flash persisted via sessionStorage) so the card reflects
        // the new URL/event list without us having to template every cell.
        this._flashAndReload("Webhook updated")
      } else {
        this._showToast("Webhook updated")
      }
    } catch (e) {
      this._showToast(e.message || "Failed to update webhook", "error")
    } finally {
      this.editSubmitTarget.disabled = false
      this.editSubmitTarget.textContent = "Save changes"
    }
  }

  // ── Delete flow ───────────────────────────────────────────────

  async confirmDelete() {
    const id = this._pendingDeleteId
    if (!id) return

    this.deleteConfirmTarget.disabled = true
    this.deleteConfirmTarget.textContent = "Deleting…"

    try {
      const url = this.destroyUrlTemplateValue.replace("ID", id)
      const response = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      if (!response.ok) throw new Error("Failed to delete webhook")

      const row = this.hasRowsTarget
        ? this.rowsTarget.querySelector(`tr[data-webhook-id="${id}"]`)
        : null
      if (row) {
        row.remove()
        if (this.hasTableTarget && !this.rowsTarget.querySelector("tr[data-webhook-id]")) {
          this.tableTarget.classList.add("hidden")
          this.emptyStateTarget.classList.remove("hidden")
        }
      }

      track("Webhook Subscription Deleted", { webhook_subscription_id: id })

      this.closeDeleteModal()
      if (this.detailModeValue) {
        this._flashAndRedirect("/webhooks", "Webhook deleted")
      } else {
        this._showToast("Webhook deleted")
      }
    } catch (e) {
      this._showToast(e.message || "Failed to delete webhook", "error")
    } finally {
      this.deleteConfirmTarget.disabled = false
      this.deleteConfirmTarget.textContent = "Delete webhook"
    }
  }

  // ── Reenable flow ─────────────────────────────────────────────

  async confirmReenable() {
    const id = this._pendingReenableId
    if (!id) return

    this.reenableConfirmTarget.disabled = true
    this.reenableConfirmTarget.textContent = "Reenabling…"

    try {
      const url = this.reenableUrlTemplateValue.replace("ID", id)
      const response = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error((json.errors && json.errors.join(", ")) || "Failed to reenable webhook")
      }

      if (this.hasRowsTarget && json.row_html) {
        this._insertOrReplaceRow(json.id, json.row_html)
      }

      track("Webhook Subscription Reenabled", { webhook_subscription_id: id })

      this.closeReenableModal()
      if (this.detailModeValue) {
        this._flashAndReload("Webhook reenabled")
      } else {
        this._showToast("Webhook reenabled — future events will deliver again")
      }
    } catch (e) {
      this._showToast(e.message || "Failed to reenable webhook", "error")
    } finally {
      this.reenableConfirmTarget.disabled = false
      this.reenableConfirmTarget.textContent = "Reenable"
    }
  }

  // ── Copy-to-clipboard (secret reveal modal) ────────────────────

  async copySecret() {
    const value = this.revealInputTarget.value
    try {
      await navigator.clipboard.writeText(value)
      this.copyLabelTarget.textContent = "Copied!"
      this.copyButtonTarget.classList.add("bg-green-600", "hover:bg-green-700")
      this.copyButtonTarget.classList.remove("bg-brand", "hover:bg-brand-dark")
      setTimeout(() => this._resetCopyButton(), 1500)
    } catch {
      this.revealInputTarget.select()
      this._showToast("Press Cmd+C to copy", "info")
    }
  }

  // ── Event-type group "All X" toggler ─────────────────────────

  toggleGroup(event) {
    const { group, formPrefix } = event.params
    const container = event.target.closest("form") || this.element
    const checked = event.target.checked
    container
      .querySelectorAll(
        `input[type="checkbox"][data-group="${group}"][data-form-prefix="${formPrefix}"]`
      )
      .forEach(cb => { cb.checked = checked })
  }

  // ── Test delivery (detail page) ───────────────────────────────

  async sendTestDelivery() {
    if (!this.hasTestButtonTarget) return

    const eventType = this.hasTestEventTypeTarget ? this.testEventTypeTarget.value : null
    const body = eventType ? JSON.stringify({ event_type: eventType }) : "{}"

    this.testButtonTarget.disabled = true
    this.testButtonLabelTarget.textContent = "Sending…"
    this._hideTestResult()

    try {
      const response = await fetch(this.deliverTestUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(json.error || "Failed to enqueue test delivery")

      this._showTestResult(
        `Enqueued test delivery for <code class="font-mono">${json.event_type}</code> ` +
        `(event_id <code class="font-mono">${json.event_id}</code>). Watching for the delivery…`,
        "info"
      )

      track("Webhook Test Delivery Sent", {
        webhook_subscription_id: this.subscriptionIdValue,
        event_type: json.event_type
      })

      this._pollForDelivery(json.event_id)
    } catch (e) {
      this._showTestResult(e.message || "Failed to enqueue test delivery", "error")
    } finally {
      this.testButtonTarget.disabled = false
      this.testButtonLabelTarget.textContent = "Send test"
    }
  }

  async _pollForDelivery(eventId) {
    const deadline = Date.now() + 15000
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 1500))
      await this.refreshDeliveries({ silent: true })
      if (!this.hasDeliveryRowsTarget) continue

      const match = Array.from(
        this.deliveryRowsTarget.querySelectorAll("tr[data-webhook-delivery-id]")
      ).find(r => (r.getAttribute("data-webhook-event-id") || "") === eventId)
      if (match) {
        this._showTestResult("Test delivery landed — see the row highlighted below.", "success")
        match.classList.add("ring-1", "ring-brand", "bg-brand/5")
        setTimeout(() => match.classList.remove("ring-1", "ring-brand", "bg-brand/5"), 4000)
        return
      }
    }
    this._showTestResult(
      "Test delivery enqueued but no attempt has landed yet — check Sidekiq's :webhooks queue.",
      "warn"
    )
  }

  // ── Deliveries table (detail page) ────────────────────────────

  applyFilters() { this.refreshDeliveries() }

  async refreshDeliveries(opts = {}) {
    if (!this.hasDeliveryRowsTarget) return

    const params = new URLSearchParams()
    if (this.hasFilterEventTypeTarget && this.filterEventTypeTarget.value) {
      params.set("event_type", this.filterEventTypeTarget.value)
    }
    if (this.hasFilterStatusTarget && this.filterStatusTarget.value) {
      params.set("status", this.filterStatusTarget.value)
    }

    const url = params.toString()
      ? `${this.deliveriesUrlValue}?${params}`
      : this.deliveriesUrlValue

    try {
      const response = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!response.ok) throw new Error("Failed to load deliveries")
      const json = await response.json()

      this.deliveryRowsTarget.innerHTML = json.deliveries.map(d => d.row_html).join("")

      if (json.deliveries.length === 0) {
        this.deliveriesTableWrapTarget.classList.add("hidden")
        this.deliveriesEmptyTarget.classList.remove("hidden")
      } else {
        this.deliveriesTableWrapTarget.classList.remove("hidden")
        this.deliveriesEmptyTarget.classList.add("hidden")
      }
    } catch (e) {
      if (!opts.silent) this._showToast(e.message || "Failed to load deliveries", "error")
    }
  }

  async toggleDeliveryDetail(event) {
    const tr = event.target.closest("tr[data-webhook-delivery-id]")
    if (!tr) return
    // Clicking the Retry button should not open the modal
    if (event.target.closest("button")) return

    const id = tr.getAttribute("data-webhook-delivery-id")
    if (!id) return
    this._openDeliveryId = id

    try {
      const url = this.deliveryShowUrlTemplateValue.replace("ID", id)
      const response = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!response.ok) throw new Error("Failed to load delivery")
      const d = await response.json()

      this.deliveryModalMetaTarget.textContent =
        `${d.event_type} · attempt #${d.attempt_number} · ${d.event_id}`
      this.deliveryRequestBodyTarget.textContent = this._prettyJson(d.request_body)
      this.deliveryResponseBodyTarget.textContent = d.response_body_preview || "(empty)"
      if (d.error_message || d.error_class) {
        this.deliveryErrorTarget.classList.remove("hidden")
        this.deliveryErrorBodyTarget.textContent =
          [d.error_class, d.error_message].filter(Boolean).join(": ")
      } else {
        this.deliveryErrorTarget.classList.add("hidden")
      }
      this.deliveryModalTarget.classList.remove("hidden")
    } catch (e) {
      this._showToast(e.message || "Failed to load delivery", "error")
    }
  }

  closeDeliveryModal() {
    this.deliveryModalTarget.classList.add("hidden")
  }

  async retryDelivery(event) {
    event.stopPropagation()
    const id = event.params.id
    if (!id) return
    await this._retryDeliveryById(id)
  }

  async retryFromModal() {
    if (!this._openDeliveryId) return
    await this._retryDeliveryById(this._openDeliveryId)
    this.closeDeliveryModal()
  }

  async _retryDeliveryById(id) {
    try {
      const url = this.deliveryRetryUrlTemplateValue.replace("ID", id)
      const response = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(json.error || "Failed to retry delivery")

      track("Webhook Delivery Retried", {
        webhook_subscription_id: this.subscriptionIdValue,
        webhook_delivery_id: id,
        event_type: json.event_type
      })

      this._showToast("Retry enqueued — new attempt will appear in the log shortly")
      setTimeout(() => this.refreshDeliveries({ silent: true }), 2000)
    } catch (e) {
      this._showToast(e.message || "Failed to retry delivery", "error")
    }
  }

  // ── Helpers ───────────────────────────────────────────────────

  _insertOrReplaceRow(id, html) {
    if (!this.hasRowsTarget) return
    const existing = this.rowsTarget.querySelector(`tr[data-webhook-id="${id}"]`)
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    const newRow = template.content.firstElementChild
    if (!newRow) return

    if (existing) {
      existing.replaceWith(newRow)
    } else {
      this.rowsTarget.prepend(newRow)
    }

    if (this.hasEmptyStateTarget && !this.emptyStateTarget.classList.contains("hidden")) {
      this.emptyStateTarget.classList.add("hidden")
      this.tableTarget.classList.remove("hidden")
    }
  }

  _resetCopyButton() {
    if (!this.hasCopyLabelTarget) return
    this.copyLabelTarget.textContent = "Copy"
    this.copyButtonTarget.classList.remove("bg-green-600", "hover:bg-green-700")
    this.copyButtonTarget.classList.add("bg-brand", "hover:bg-brand-dark")
  }

  _syncAllGroupCheckboxes(container) {
    container.querySelectorAll('input[type="checkbox"][data-webhooks-group-param]')
      .forEach(allCb => {
        const group = allCb.getAttribute("data-webhooks-group-param")
        const prefix = allCb.getAttribute("data-webhooks-form-prefix-param")
        const groupCbs = container.querySelectorAll(
          `input[type="checkbox"][data-group="${group}"][data-form-prefix="${prefix}"]`
        )
        allCb.checked = groupCbs.length > 0 &&
          Array.from(groupCbs).every(cb => cb.checked)
      })
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  _prettyJson(text) {
    if (!text) return "(empty)"
    try {
      return JSON.stringify(JSON.parse(text), null, 2)
    } catch {
      return text
    }
  }

  _showTestResult(message, kind = "info") {
    if (!this.hasTestResultTarget) return
    const palette = {
      success: "bg-green-50 border-green-200 text-green-800",
      info:    "bg-blue-50 border-blue-200 text-blue-800",
      warn:    "bg-amber-50 border-amber-200 text-amber-800",
      error:   "bg-red-50 border-red-200 text-red-800"
    }[kind] || "bg-gray-50 border-gray-200 text-gray-800"
    this.testResultTarget.className =
      `mt-3 p-3 rounded-lg text-[13px] border ${palette}`
    this.testResultTarget.innerHTML = message
  }

  _hideTestResult() {
    if (!this.hasTestResultTarget) return
    this.testResultTarget.classList.add("hidden")
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-[13px]"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _flashAndReload(message) {
    sessionStorage.setItem("webhooks:pendingFlash", JSON.stringify({ message, type: "notice" }))
    window.location.reload()
  }

  _flashAndRedirect(url, message) {
    sessionStorage.setItem("webhooks:pendingFlash", JSON.stringify({ message, type: "notice" }))
    window.location.assign(url)
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("webhooks:pendingFlash")
    if (!raw) return
    sessionStorage.removeItem("webhooks:pendingFlash")
    try {
      const { message, type } = JSON.parse(raw)
      if (message) this._showToast(message, type === "alert" ? "error" : "success")
    } catch { /* noop */ }
  }
}
